import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function LaborShortageParadox() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="The labor shortage paradox: why roofing companies turn down money"
            date="March 25, 2026"
            readTime="6 MIN"
            heroImage="/images/blog/labor-shortage-paradox-hero.jpg"
            description="78% of roofing contractors expect higher sales in 2026. 62% have unfilled capacity. The paradox is not a labor problem. It is a systems problem."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A roofing company in Denver. 12 crews. Six-week booking.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Last week they turned down 6 estimates. They had the crews. They had the time slots. They had the capacity to make $60K.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                They turned it down anyway.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Not because they couldn't handle the work. Because nobody could handle the <em>admin around</em> the work.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That is not a labor shortage. That is a systems problem.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">The numbers that don't add up</h2>

            <p className="mb-4 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The data seems contradictory:
            </p>

            <ul className="mb-6 space-y-2 text-[#F0EFF4]/80 text-lg leading-relaxed list-disc list-inside">
                <li>78% of roofing contractors expect higher sales in 2026</li>
                <li>73% say labor shortage is their number one pain</li>
                <li>62% have unfilled capacity they cannot fulfill</li>
            </ul>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                How do you simultaneously lack workers and leave money on the table?
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The answer: the shortage is not in labor. It is in system capacity.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">What actually happens inside that office</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here is the real sequence at that Denver company:
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Crew finishes a job Friday. Invoice goes out Saturday. Follow-up email lands Tuesday. Customer compares quotes Wednesday. Still no signed contract.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Monday afternoon, a new lead calls.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The office manager is on a call with a supplier. The project manager is out doing a site review. The owner is doing payroll from home. The phone rings four times and goes to voicemail.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed italic">
                "Hi, we are interested in a roof inspection. Please call back."
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Owner checks voicemail at 6 PM. Calls back at 6:15. The prospect is eating dinner. "I'll call you tomorrow."
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Next morning, the prospect gets two other quotes before breakfast. That company is now third in line.
            </p>

            <h3 className="text-xl font-bold text-[#F0EFF4] mb-4">Why this keeps happening</h3>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It is not incompetence. It is friction.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Intake is manual. Someone has to answer the phone, log the info, route it to an inspector, and schedule the visit. Proposals are manual. Inspector submits photos. Someone processes them, pulls weather data, builds the quote, emails it, and follows up two days later. Handoffs break constantly. There is no automatic signal when a step finishes. Everyone checks up on everyone.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                And it scales badly. Eight crews is manageable. Twelve crews means every admin task takes 50% longer because the same two people are doing it all.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The company is not short on workers. It is short on admin capacity.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">The cost of slow</h2>

            <p className="mb-4 text-[#F0EFF4]/80 text-lg leading-relaxed">
                From March 2026 industry data:
            </p>

            <ul className="mb-6 space-y-2 text-[#F0EFF4]/80 text-lg leading-relaxed list-disc list-inside">
                <li>13,600+ unfilled roofer positions across the U.S.</li>
                <li>Average lead response time: 18 hours (the fastest contractors respond in under 30 minutes)</li>
                <li>Average quote turnaround: 5 to 7 days (the fastest deliver in 24 hours)</li>
                <li>40% of quotes receive no follow-up at all</li>
                <li>Lost revenue from slow response: roughly $42,000 per contractor, per year</li>
            </ul>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That $42,000 is not a labor problem. The crews exist. The work exists. The customer called.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The gap is in the hours between "customer called" and "someone got back to them."
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">Side by side</h2>

            <div className="mb-8 overflow-x-auto">
                <table className="w-full text-[#F0EFF4]/80 text-base border-collapse">
                    <thead>
                        <tr className="border-b border-[#F0EFF4]/20">
                            <th className="text-left py-3 pr-6 font-semibold text-[#F0EFF4]">Task</th>
                            <th className="text-left py-3 pr-6 font-semibold text-[#F0EFF4]">Most contractors today</th>
                            <th className="text-left py-3 font-semibold text-[#F0EFF4]">Fastest contractors</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-[#F0EFF4]/10">
                            <td className="py-3 pr-6">Lead intake</td>
                            <td className="py-3 pr-6">Manual phone, manual log</td>
                            <td className="py-3">Auto: SMS to CRM in seconds</td>
                        </tr>
                        <tr className="border-b border-[#F0EFF4]/10">
                            <td className="py-3 pr-6">Lead response</td>
                            <td className="py-3 pr-6">18 hours</td>
                            <td className="py-3">Under 30 minutes</td>
                        </tr>
                        <tr className="border-b border-[#F0EFF4]/10">
                            <td className="py-3 pr-6">Photo to proposal</td>
                            <td className="py-3 pr-6">5 to 7 days</td>
                            <td className="py-3">24 hours</td>
                        </tr>
                        <tr className="border-b border-[#F0EFF4]/10">
                            <td className="py-3 pr-6">Proposal follow-up</td>
                            <td className="py-3 pr-6">Manual email, maybe</td>
                            <td className="py-3">4 touches over 12 days</td>
                        </tr>
                        <tr>
                            <td className="py-3 pr-6">Invoice to payment</td>
                            <td className="py-3 pr-6">10 to 14 days</td>
                            <td className="py-3">7 days</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every row is admin work. Every row delays revenue. With 12 crews, every delay compounds.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">The math on Denver</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The Denver company has 12 crews and turns down 6 estimates a month.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Average job: $8,000. Six lost leads per month: $48,000. Over twelve months: $576,000 in preventable losses.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Fixing this by hiring an office coordinator: $50,000 per year.
                Fixing this by automating the three broken steps: around $500 per month.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The math is not complicated.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">Three fixes, no new hires</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                None of these require new crews or new employees.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Automate intake. Lead comes in by phone, form, or text. It goes straight into the CRM. Inspector is notified before the truck moves. Confirmation SMS goes back to the customer in under two minutes. Response time drops from 18 hours to under 30 minutes.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Automate proposals. Inspector photos trigger a processing workflow. Weather data is pulled automatically. The proposal is generated and sent within 24 hours, without anyone manually building it. Five to seven days drops to next-day delivery.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Automate follow-up. Quote sent. Automatic follow-up at day two. Another at day five. Another at day ten. SMS reminder at day twelve. If they sign, the sequence stops. If they go cold, they are filed and you move on. Follow-up rate goes from 40% to 70%.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">This is what RoofClaw does</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Not by hiring someone. By running those three workflows for you.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Same 12 crews. Same six-week booking. But every lead gets a response in under two hours. Every quote gets followed up four times. Every prospect gets a reminder before they choose someone else.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                12 crews plus system capacity equals $576,000 in recovered revenue.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That is not theory. That is arithmetic.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-4">The window is now</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Spring 2026 is peak season. Twenty-three percent of homeowners are planning re-roofs in the next twelve months. The leads are coming whether your systems are ready or not.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The labor gap is real. But it is not why companies turn down $60,000 in a single week.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Systems are.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Close the gap. Recover the revenue.
            </p>

            <hr className="border-[#F0EFF4]/10 my-8" />

            <p className="mb-4 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Want to see exactly where your revenue is leaking? Book a 15-minute call and we will walk through your intake-to-close process and show you what the numbers look like on your end.
            </p>

            <a
                href="https://roofclaw.com/contact"
                className="inline-block bg-[#6C63FF] hover:bg-[#5a52e0] text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
                Book a call with RoofClaw
            </a>
        </BlogLayout>
    );
}
